import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/* ═══════════════════════════════════════════════════════════
   RATE TYPE — exported so any component (GroceryStoreHome, etc.)
   can type a listing's pricing mode without redefining it.
═══════════════════════════════════════════════════════════ */
export type RateType = "perProduct" | "perItem";

export const DEFAULT_RATE_TYPE: RateType = "perProduct";

/* ═══════════════════════════════════════════════════════════
   LINE TOTAL — the single place unit price × qty gets resolved
   for a food/grocery cart line. Both rate types currently behave
   the same (unit already includes size/extras), but kept distinct
   so future divergence (e.g. perItem multiplying variants) has
   a home without touching every call site.
═══════════════════════════════════════════════════════════ */
export const calculateLineTotal = (
  unitRate: number,
  quantity: number,
  rateType: RateType = DEFAULT_RATE_TYPE
): number => {
  const safeRate = unitRate ?? 0;
  const safeQty = Math.max(1, quantity ?? 1);

  const total =
    rateType === "perProduct"
      ? safeRate * safeQty
      : safeRate * safeQty;

  return parseFloat(total.toFixed(2));
};

export interface FoodOrderVariant {
  colorId?: string;
  portionId?: string;
  name: string;
  hex?: string | null;
  rate: number;
}

export interface FoodOrderExtra {
  id: string;
  name: string;
  rate: number;
}

export interface FoodOrderItem {
  id: string;
  listingId: string;
  name: string;
  rate: number | null;
  rateType: RateType;
  quantity: number;
  selectedVariants: FoodOrderVariant[];
  extras: FoodOrderExtra[];
}

export interface FoodServiceFeeState {
  items: FoodOrderItem[];
  guestFee: number;
  smallOrderFee: number;
  totalCharged: number;
}

const initialState: FoodServiceFeeState = {
  items: [],
  guestFee: 0,
  smallOrderFee: 0,
  totalCharged: 0,
};

/* ═══════════════════════════════════════════════════════════
   FEE TIER HELPERS — same ladder shape, different %, so each
   fee type is independently tunable without touching the others.
═══════════════════════════════════════════════════════════ */
export const calculateHostEarnings = (total: number) => {
  let percentage: number;
  if (total > 5000) percentage = 0.2;
  else if (total > 1000) percentage = 0.15;
  else if (total > 500) percentage = 0.12;
  else if (total > 100) percentage = 0.1;
  else percentage = 0.08;
  const hostsFee = parseFloat((total * percentage).toFixed(2));
  const hostProfit = parseFloat((total - hostsFee).toFixed(2));
  return { hostProfit, hostsFee };
};

export const calculateGuestServiceFee = (subtotal: number): number => {
  let guestsFee: number;
  if (subtotal > 5000) guestsFee = subtotal * 0.2;
  else if (subtotal > 1000) guestsFee = subtotal * 0.15;
  else if (subtotal > 500) guestsFee = subtotal * 0.12;
  else if (subtotal > 100) guestsFee = subtotal * 0.1;
  else guestsFee = subtotal * 0.08;
  return parseFloat(guestsFee.toFixed(2));
};

export const calculateSmallOrderFee = (total: number): number => {
  if (total > 0 && total < 150) return parseFloat((total * 0.05).toFixed(2));
  return 0;
};

export const calculateCancellationFee = (total: number): number => {
  let percentage: number;
  if (total > 5000) percentage = 0.08;
  else if (total > 1000) percentage = 0.07;
  else if (total > 500) percentage = 0.06;
  else if (total > 100) percentage = 0.05;
  else percentage = 0.04;
  return parseFloat((total * percentage).toFixed(2));
};

export const calculateSchedulingFee = (total: number): number => {
  let percentage: number;
  if (total > 5000) percentage = 0.06;
  else if (total > 1000) percentage = 0.05;
  else if (total > 500) percentage = 0.04;
  else if (total > 100) percentage = 0.03;
  else percentage = 0.02;
  return parseFloat((total * percentage).toFixed(2));
};

/* ═══════════════════════════════════════════════════════════
   TOTAL — mirrors orderSlice's calculateTotal but uses
   calculateLineTotal so the "one place" rule holds for food carts.
═══════════════════════════════════════════════════════════ */
const calculateTotal = (state: FoodServiceFeeState) => {
  let total = 0;

  state.items.forEach((item) => {
    let itemTotal = 0;

    if (item.rateType === "perProduct") {
      itemTotal = calculateLineTotal(item.rate ?? 0, item.quantity, item.rateType);
    } else {
      if (item.selectedVariants.length > 0) {
        item.selectedVariants.forEach((variant) => {
          itemTotal += calculateLineTotal(variant.rate ?? 0, item.quantity, item.rateType);
        });
      } else {
        itemTotal = calculateLineTotal(item.rate ?? 0, item.quantity, item.rateType);
      }
    }

    total += itemTotal;

    item.extras.forEach((extra) => {
      total += calculateLineTotal(extra.rate ?? 0, item.quantity, item.rateType);
    });
  });

  total += state.guestFee + state.smallOrderFee;

  state.totalCharged = Math.round(total * 100) / 100;
};

const foodServiceFeeSlice = createSlice({
  name: 'foodServiceFee',
  initialState,
  reducers: {
    addItem: (
      state,
      action: PayloadAction<{
        listingId: string;
        name: string;
        rate: number | null;
        rateType: RateType;
        quantity: number;
      }>
    ) => {
      const { listingId, name, rate, rateType, quantity } = action.payload;
      const lineId = `${Date.now()}-${Math.random()}`;
      state.items.push({
        id: lineId,
        listingId,
        name,
        rate,
        rateType,
        quantity,
        selectedVariants: [],
        extras: [],
      });
      calculateTotal(state);
    },

    removeItem: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter((item) => item.id !== action.payload);
      calculateTotal(state);
    },

    updateQuantity: (
      state,
      action: PayloadAction<{ itemId: string; quantity: number }>
    ) => {
      const item = state.items.find((i) => i.id === action.payload.itemId);
      if (item) {
        item.quantity = Math.max(1, action.payload.quantity);
        calculateTotal(state);
      }
    },

    addVariant: (
      state,
      action: PayloadAction<{ itemId: string; variant: FoodOrderVariant }>
    ) => {
      const item = state.items.find((i) => i.id === action.payload.itemId);
      if (item) {
        const variantKey = action.payload.variant.colorId || action.payload.variant.portionId;
        const exists = item.selectedVariants.some(
          (v) => v.colorId === variantKey || v.portionId === variantKey
        );
        if (!exists) {
          item.selectedVariants.push(action.payload.variant);
          calculateTotal(state);
        }
      }
    },

    removeVariant: (
      state,
      action: PayloadAction<{ itemId: string; variantKey: string }>
    ) => {
      const item = state.items.find((i) => i.id === action.payload.itemId);
      if (item) {
        item.selectedVariants = item.selectedVariants.filter(
          (v) => v.colorId !== action.payload.variantKey && v.portionId !== action.payload.variantKey
        );
        calculateTotal(state);
      }
    },

    addExtra: (
      state,
      action: PayloadAction<{ itemId: string; extra: FoodOrderExtra }>
    ) => {
      const item = state.items.find((i) => i.id === action.payload.itemId);
      if (item) {
        const exists = item.extras.some((e) => e.id === action.payload.extra.id);
        if (!exists) {
          item.extras.push(action.payload.extra);
          calculateTotal(state);
        }
      }
    },

    removeExtra: (
      state,
      action: PayloadAction<{ itemId: string; extraId: string }>
    ) => {
      const item = state.items.find((i) => i.id === action.payload.itemId);
      if (item) {
        item.extras = item.extras.filter((e) => e.id !== action.payload.extraId);
        calculateTotal(state);
      }
    },

    setGuestFee: (state, action: PayloadAction<number>) => {
      state.guestFee = Math.max(0, action.payload);
      calculateTotal(state);
    },

    setSmallOrderFee: (state, action: PayloadAction<number>) => {
      state.smallOrderFee = Math.max(0, action.payload);
      calculateTotal(state);
    },

    clearOrder: (state) => {
      state.items = [];
      state.guestFee = 0;
      state.smallOrderFee = 0;
      state.totalCharged = 0;
    },
  },
});

export const {
  addItem,
  removeItem,
  updateQuantity,
  addVariant,
  removeVariant,
  addExtra,
  removeExtra,
  setGuestFee,
  setSmallOrderFee,
  clearOrder,
} = foodServiceFeeSlice.actions;

export const selectFoodServiceFeeState = (state: { foodServiceFee: FoodServiceFeeState }) =>
  state.foodServiceFee;
export const selectFoodOrderItems = (state: { foodServiceFee: FoodServiceFeeState }) =>
  state.foodServiceFee.items;
export const selectGuestFee = (state: { foodServiceFee: FoodServiceFeeState }) =>
  state.foodServiceFee.guestFee;
export const selectSmallOrderFee = (state: { foodServiceFee: FoodServiceFeeState }) =>
  state.foodServiceFee.smallOrderFee;
export const selectTotalCharged = (state: { foodServiceFee: FoodServiceFeeState }) =>
  state.foodServiceFee.totalCharged;
export const selectAllFees = (state: { foodServiceFee: FoodServiceFeeState }) => ({
  guestFee: state.foodServiceFee.guestFee,
  smallOrderFee: state.foodServiceFee.smallOrderFee,
  total: state.foodServiceFee.totalCharged,
});

export default foodServiceFeeSlice.reducer;